import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, ArrowRight, TrendingUp, Radar, FileCheck2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { ROLE_LABEL } from '@/lib/format';
import api from '@/lib/api';
import { toast } from 'sonner';
import recoupLogo from '@/assets/recoup-logo.png';

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" />
  </svg>
);

const MicrosoftIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('pm@apex.com');
  const [password, setPassword] = useState('Passw0rd!');
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('project_manager');
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState([]);
  const [oauth, setOauth] = useState(null);

  useEffect(() => { if (user) navigate('/'); }, [user]);
  useEffect(() => { api.get('/auth/demo-users').then((r) => setDemo(r.data.users || [])).catch(() => {}); }, []);
  useEffect(() => { api.get('/auth/oauth-config').then((r) => setOauth(r.data)).catch(() => {}); }, []);

  const isSignup = mode === 'signup';

  // Kick off the Google authorization-code redirect flow.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    if (!oauth?.google?.enabled) return;
    const redirectUri = window.location.origin + '/auth/google';
    const params = new URLSearchParams({
      client_id: oauth.google.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      include_granted_scopes: 'true',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const startMicrosoft = () => {
    if (!oauth?.microsoft?.enabled) {
      toast.info('Microsoft sign-in will be enabled once its credentials are added.');
      return;
    }
    const redirectUri = window.location.origin + '/auth/microsoft';
    const tenant = oauth.microsoft.tenant || 'common';
    const params = new URLSearchParams({
      client_id: oauth.microsoft.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile User.Read',
      response_mode: 'query',
      prompt: 'select_account',
    });
    window.location.href = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?${params.toString()}`;
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === 'signup') { setEmail(''); setPassword(''); }
    else { setEmail('pm@apex.com'); setPassword('Passw0rd!'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignup) {
        if (!companyName.trim() || !name.trim()) { toast.error('Company and your name are required'); setLoading(false); return; }
        await register({ company_name: companyName, name, email, password, role });
        toast.success('Workspace created');
      } else {
        await login(email, password);
        toast.success('Welcome back');
      }
      navigate('/');
    } catch (err) { toast.error(err?.response?.data?.detail || (isSignup ? 'Sign up failed' : 'Login failed')); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#F6F7F9]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-ink-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
          style={{ background: 'radial-gradient(620px circle at 18% 12%, rgba(51,88,212,0.22), transparent 45%), radial-gradient(560px circle at 88% 92%, rgba(21,147,92,0.18), transparent 45%)' }} />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="relative flex items-center">
          <img src={recoupLogo} alt="Recoup" className="h-10 w-auto select-none" draggable="false" data-testid="login-logo" />
        </div>
        <div className="relative max-w-lg">
          <h1 className="text-[42px] leading-[1.05] font-semibold tracking-tight font-display">Get paid for the work you&rsquo;ve already done.</h1>
          <p className="mt-5 text-[15px] text-slate-300 leading-relaxed">AI monitors every email, drawing, RFI and field report &mdash; detecting scope changes the moment they happen and assembling submission-ready change orders before they&rsquo;re forgotten or disputed.</p>
          <div className="mt-8 space-y-3">
            {[[Radar, 'Detects extra work automatically across every source'], [FileCheck2, 'Generates defensible, itemized change orders in one click'], [TrendingUp, 'Surfaces dollars recovered, at risk, and in dispute']].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-200">
                <div className="rounded-lg bg-white/[0.06] p-1.5 ring-1 ring-white/10"><Icon className="h-4 w-4 text-money" /></div>{t}
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-8">
            <div><div className="num-lg text-3xl text-money">$2M+</div><div className="text-[11px] text-slate-400 uppercase tracking-wider mt-1">Contracts monitored</div></div>
            <div className="h-10 w-px bg-white/10" />
            <div><div className="num-lg text-3xl text-white">48%</div><div className="text-[11px] text-slate-400 uppercase tracking-wider mt-1">Recovery rate</div></div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">Revenue-recovery &amp; margin-protection for commercial contractors.</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="rounded-lg bg-money p-1.5"><ShieldCheck className="h-5 w-5 text-white" /></div>
            <span className="font-semibold text-ink-900 font-display">Recoup</span>
          </div>
          <h2 className="text-[26px] font-semibold text-ink-900 font-display tracking-tight">{isSignup ? 'Create your workspace' : 'Sign in'}</h2>
          <p className="text-sm text-slate-500 mt-1.5">{isSignup ? 'Start evaluating Recoup on your own projects in minutes.' : 'Access your revenue-recovery workspace.'}</p>

          {/* Social login / signup */}
          <div className="mt-7 space-y-2.5" data-testid="social-auth">
            <button type="button" onClick={startGoogle} disabled={!oauth?.google?.enabled}
              data-testid="google-login-btn"
              className="w-full h-11 inline-flex items-center justify-center gap-2.5 rounded-md border border-[#E2E5EA] bg-white text-sm font-semibold text-ink-900 transition-colors duration-150 hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-50 disabled:cursor-not-allowed">
              <GoogleIcon className="h-[18px] w-[18px]" />
              {isSignup ? 'Sign up with Google' : 'Continue with Google'}
            </button>
            <button type="button" onClick={startMicrosoft} disabled={!oauth?.microsoft?.enabled}
              data-testid="microsoft-login-btn"
              title={oauth?.microsoft?.enabled ? '' : 'Microsoft sign-in coming soon'}
              className="w-full h-11 inline-flex items-center justify-center gap-2.5 rounded-md border border-[#E2E5EA] bg-white text-sm font-semibold text-ink-900 transition-colors duration-150 hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-50 disabled:cursor-not-allowed">
              <MicrosoftIcon className="h-[18px] w-[18px]" />
              {isSignup ? 'Sign up with Microsoft' : 'Continue with Microsoft'}
            </button>
          </div>

          <div className="flex items-center gap-3 my-6 text-[11px] uppercase tracking-wider text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />or {isSignup ? 'sign up' : 'sign in'} with email<div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <>
                <div>
                  <Label htmlFor="company" className="text-xs font-semibold text-slate-600">Company name</Label>
                  <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="signup-company" className="mt-1.5 h-11 bg-white" placeholder="e.g. Apex Construction" required />
                </div>
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold text-slate-600">Your name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="signup-name" className="mt-1.5 h-11 bg-white" placeholder="e.g. Jordan Miles" required />
                </div>
                <div>
                  <Label htmlFor="role" className="text-xs font-semibold text-slate-600">Your role</Label>
                  <select id="role" value={role} onChange={(e) => setRole(e.target.value)} data-testid="signup-role"
                    className="mt-1.5 h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
                    {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-600">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" className="mt-1.5 h-11 bg-white" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-600">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="mt-1.5 h-11 bg-white" required />
            </div>
            <Button type="submit" disabled={loading} data-testid={isSignup ? 'signup-submit' : 'login-submit'} className="w-full h-11 text-[14px] group">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {isSignup ? 'Create workspace' : 'Sign in'} <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="mt-4 text-center text-sm text-slate-500" data-testid="auth-mode-toggle">
            {isSignup ? (
              <>Already have a workspace?{' '}
                <button type="button" onClick={() => switchMode('login')} className="font-semibold text-brand hover:underline">Sign in</button>
              </>
            ) : (
              <>New to Recoup?{' '}
                <button type="button" onClick={() => switchMode('signup')} className="font-semibold text-brand hover:underline" data-testid="go-signup">Create a workspace</button>
              </>
            )}
          </div>

          {!isSignup && demo.length > 0 && (
            <div className="mt-9">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400 mb-3">
                <div className="h-px flex-1 bg-slate-200" />Demo accounts<div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {demo.map((u) => (
                  <button key={u.id} type="button" data-testid={`demo-${u.role}`}
                    onClick={() => { setEmail(u.email); setPassword('Passw0rd!'); }}
                    className="text-left rounded-xl border border-[#ECEEF2] bg-white px-3 py-2.5 hover:border-brand/40 hover:shadow-soft transition-all duration-150">
                    <div className="text-xs font-semibold text-ink-900">{ROLE_LABEL[u.role]}</div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-slate-400 mt-3 text-center">Password for all demo accounts: <span className="font-mono text-slate-500">Passw0rd!</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
